import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { clone, filter, includes, map, reverse } from 'lodash';
import { Observable } from 'rxjs';
import { ISubscription } from 'rxjs/Subscription';
import { addDynamicColumns } from 'Src/app/shared/sorter-column/flattened-student-map.constant.js';
import { ToggleService } from 'Src/ng2/shared/services/toggle/toggle.service';
import { ISchoolAssessment } from 'Src/ng2/shared/typings/interfaces/school-assessment.interface';
import { CreateSyncTool, ICreateSyncToolPayload } from 'Src/ng2/store';
import { IACOption } from '../../../typings/interfaces/design-library.interface';
import { BaseModalComponent } from '../../base-modal.component';
import { Toggles } from './../../../../shared/constants/toggles.constant';
import { takeUntil } from 'rxjs/operators';

export interface ISheetModalColumnsComponentData {
  title: string;
  message: string;
  confirmText?: string;
}

export interface IColOption {
  humanName: string;
  tag: string;
  keyName: string;
}

export interface ISelectedCol {
  human: string;
  key: string;
  canDelete: boolean;
}
@Component({
  template: require('./synced-sheet-columns-modal.component.html'),
  styles: [require('./synced-sheet-columns-modal.component.scss')],
})
export class SyncedSheetColumnsModalComponent extends BaseModalComponent implements OnInit, OnDestroy {
  @ViewChild('rowContainer', { static: false }) rowContainer: ElementRef;
  form: FormGroup;
  title = 'Create new sheet';
  addColPlaceholder: string = 'Find a column to add…';
  isProfileMode: boolean = true;
  selectedCols: ISelectedCol[] = [
    { human: 'Student Name', key: 'studentName', canDelete: false },
    { human: 'Student ID', key: 'studentId', canDelete: false },
  ];
  ACOptions: IACOption[] = [];
  ACOptionsSource: IACOption[] = [];
  ACOptionsMap: Map<string, IACOption>;
  initialInputsState = {
    studentsCount: null,
    colsCount: this.selectedCols.length,
    studentsOptions: [{ value: 'All students', subOptions: [] }],
    colsOptions: [],
  };
  schoolAssessments$: Observable<ISchoolAssessment[]>;
  schoolAssessmentsSubscription: ISubscription;
  schoolAssessments: ISchoolAssessment[];
  showDangerMsg: boolean;
  small: string = 'small';
  danger: string = 'danger';
  tableShadow: boolean = false;
  buttonMode: string = 'back';
  hasClearBtn: boolean = true;
  colsOptions: IColOption[];

  constructor(
    dialogRef: MatDialogRef<SyncedSheetColumnsModalComponent>,
    public formBuilder: FormBuilder,
    private store: Store<any>,
    private toggleService: ToggleService,
    @Inject(MAT_DIALOG_DATA) public data, // : ISpecializedDataType,
  ) {
    super(dialogRef);
  }

  get createValid() {
    const { allStudents } = this.data;
    return allStudents.length * this.selectedCols.length < 2000000 ? true : false;
  }

  ngOnInit() {
    this.data.schoolAssessments$.pipe(takeUntil(this.destroy$)).subscribe(res => (this.schoolAssessments = res));
    this.form = this.formBuilder.group({
      colSearch: [null, Validators.required],
    });
    this.colsOptions = this.initColsOptions();
    this.ACOptions = this.shapeColsForAutocomplete(this.colsOptions);
    this.ACOptionsSource = clone(this.ACOptions);
    this.ACOptionsMap = new Map(this.ACOptionsSource.map(option => [option.human, option]));
    this._updateACOptions();
    this.form.valueChanges.subscribe(changes => {
      const { colSearch } = changes;
      // following prevents native select from causing an error due to conflict with storybook method also called select
      const selectSearchCondition = !!colSearch && !colSearch.bubbles;
      if (colSearch === '' || selectSearchCondition) {
        const searchTerm = colSearch.human ? colSearch.human : colSearch;
        if (!searchTerm || searchTerm === '') {
          this.ACOptions = this.ACOptionsSource;
        } else {
          this.ACOptions = filter(this.ACOptions, (el: IACOption) => {
            return includes(el.human.toLowerCase(), searchTerm.toLowerCase());
          });
        }
      }
    });
  }

  _updateACOptions(): void {
    // updates ACOptions to excluded the options in selectedColumns array
    const selectedKeys = map(this.selectedCols, col => col.key);
    this.ACOptions = filter(this.ACOptionsSource, option => !includes(selectedKeys, option.key));
  }

  columnSelected($event?): void {
    this.form.controls.colSearch.setValue(null);
    const selectedColumn = this.ACOptionsMap.get($event);
    this.selectedCols.unshift({ ...selectedColumn, canDelete: true });
    this._updateACOptions();
  }

  columnDeleted(colHuman): void {
    this.selectedCols = filter(this.selectedCols, col => {
      return col.human !== colHuman;
    });
    this._updateACOptions();
  }

  _toggleNycTMvp(toggleNycTMvp) {
    let availableCols;
    if (toggleNycTMvp) {
      availableCols = addDynamicColumns(this.schoolAssessments, toggleNycTMvp);
    } else {
      availableCols = addDynamicColumns();
    }
    return availableCols;
  }

  initColsOptions(): IColOption[] {
    const toggleNycTMvp = this.toggleService.getToggleState(Toggles.TOGGLE_NYCT_MVP);
    const allCols = this._toggleNycTMvp(toggleNycTMvp);
    const colOptions = map(
      allCols as {
        [key: string]: {
          humanName: string;
          tag: string;
          path: string;
          calculation: any;
        };
      },
      (col: any, key) => {
        const colOption = Object.assign(
          {},
          {
            keyName: key,
            tag: col.tag || 'general',
            humanName: col.humanName,
          },
        );
        if (this.selectedCols.indexOf(col.humanName) > -1) {
          this.initialInputsState.colsOptions.push(colOption);
        }
        return colOption;
      },
    );
    return colOptions;
  }

  shapeColsForAutocomplete(colOptions): IACOption[] {
    return map(colOptions, option => {
      return {
        human: option.humanName,
        key: option.keyName,
      };
    });
  }

  onCreate(): void {
    const { sheetName, schoolId } = this.data;
    const columnKeys = reverse(map(this.selectedCols, col => col.key));
    const payload: ICreateSyncToolPayload = {
      schoolId,
      spreadsheetName: sheetName,
      filter: this._concatFilterObj(),
      columnKeys,
    };
    // dispatch action to create sync
    if (!this.showDangerMsg) {
      this.store.dispatch(new CreateSyncTool(payload));
      this.close({ syncCreated: true });
    }
  }

  cancel(): void {
    this.close({ syncCreated: false });
  }

  _concatFilterObj() {
    const { studentsToInclude } = this.data;
    let filterObj;
    // 'All students' sends no $or filter to backend
    if (studentsToInclude === 'All students') {
      filterObj = {
        schoolStatus: { $in: ['A'] },
      };
    } else {
      filterObj = {
        $or: this._concatQueryFields(studentsToInclude),
        schoolStatus: { $in: ['A'] },
      };
    }
    return filterObj;
  }

  _concatQueryFields(studentsToInclude) {
    const { selectedOptions } = this.data;
    const groupings = {
      'By class': 'studentDetails.classOf',
      'By grade': 'studentDetails.grade',
      'By official class': 'studentDetails.officialClass',
    };
    const fieldName = groupings[studentsToInclude];
    const arr = map(selectedOptions, option => {
      return { [fieldName]: option };
    });
    return arr;
  }

  onTableScroll(): void {
    const scrollTop = this.rowContainer.nativeElement.scrollTop;
    this.tableShadow = scrollTop > 10;
  }

  onClear(): void {
    this.form.controls.colSearch.setValue('');
  }
}
